<!-- filepath: c:\Users\TyreseMakhubele\Documents\Other\P\trendztube\src\views\legal\PrivacyPolicy.vue -->
<template>
  <AppHeader />
  <div class="min-h-screen bg-gradient-to-br from-primary-lighter via-white to-primary-lighter pt-8 pb-20 px-4">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
          Privacy Policy
        </h1>
        <p class="text-lg text-gray-600">
          Last updated: {{ lastUpdated }}
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-2xl shadow-lg p-8 space-y-8">
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
          <p class="text-gray-700 leading-relaxed">
            TrendzTube ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered YouTube video idea generator service.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
          <ul class="text-gray-700 space-y-2 mb-4 list-disc pl-6">
            <li>Email address (for account creation and communication)</li>
            <li>Name (optional, for personalization)</li>
            <li>Profile picture (optional, stored securely in Firebase Storage)</li>
            <li>Payment information (processed securely through Lemon Squeezy)</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Usage Data</h3>
          <ul class="text-gray-700 space-y-2 mb-4 list-disc pl-6">
            <li>Number of video ideas generated</li>
            <li>Selected regions and categories</li>
            <li>Usage patterns and feature interactions</li>
            <li>Device information and browser type</li>
            <li>IP address and location data (for regional content)</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">YouTube Data</h3>
          <p class="text-gray-700 leading-relaxed">
            We access YouTube's trending videos data through their official API to provide our service. We do not store personal YouTube data or access your YouTube account.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Provide and maintain our AI-powered video idea generation service</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send service-related communications and updates</li>
            <li>Analyze usage patterns to improve our service</li>
            <li>Provide customer support</li>
            <li>Enforce usage limits based on subscription plans</li>
            <li>Prevent fraud and ensure platform security</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Firebase (Google)</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            We use Firebase for authentication, database, and file storage. Your data is protected under Google's privacy practices.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Lemon Squeezy</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Payment processing is handled by Lemon Squeezy. We do not store your payment information directly.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">YouTube API</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            We access YouTube's public trending data API to provide video recommendations. No personal YouTube data is accessed.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Google Vertex AI</h3>
          <p class="text-gray-700 leading-relaxed">
            We use Google's Vertex AI Gemini model to generate video ideas. Trending data is processed to create recommendations.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            We implement appropriate security measures to protect your personal information:
          </p>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Data encryption in transit and at rest</li>
            <li>Secure authentication with Firebase</li>
            <li>Regular security updates and monitoring</li>
            <li>Limited access to personal data by authorized personnel only</li>
            <li>HTTPS encryption for all communications</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
          <p class="text-gray-700 leading-relaxed mb-4">You have the following rights regarding your personal data:</p>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li><strong>Access:</strong> Request copies of your personal data</li>
            <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Erasure:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Request transfer of your data</li>
            <li><strong>Objection:</strong> Object to processing of your personal data</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
          <p class="text-gray-700 leading-relaxed">
            We retain your personal information only for as long as necessary to provide our services and comply with legal obligations. Account data is deleted within 30 days of account deletion request.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Cookies and Analytics</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            We use Google Analytics to understand how our service is used. We also use necessary cookies for:
          </p>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Authentication and session management</li>
            <li>User preferences and settings</li>
            <li>Service functionality and performance</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
          <p class="text-gray-700 leading-relaxed">
            Our service is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Changes to Privacy Policy</h2>
          <p class="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p class="text-gray-700 leading-relaxed">
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div class="bg-gray-50 rounded-lg p-4 mt-4">
            <p class="text-gray-700">
              Email: <a href="mailto:tyteapps@gmail.com" class="text-primary hover:underline">tyteapps@gmail.com</a><br>
              Subject: Privacy Policy Inquiry
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
  <AppFooter />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import AppFooter from '@/components/common/AppFooter.vue';

export default {
  name: 'PrivacyPolicy',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      lastUpdated: 'November 8, 2025'
    };
  }
};
</script>

<style scoped>
section {
  scroll-margin-top: 2rem;
}
</style>